import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { loadPaySdkScript, getPaySdkContext } from 'helper/pay-sdk-helper';
import {
  isGrabIDAuthenticated,
  getPaxAccessToken,
} from 'helper/grab-id-auth-helper';
import { isAuthHintCookie } from 'helper/session-helper';
import { userInfoSelector } from 'selectors/app-selector';
import { isProduction, isDev } from 'common/utils/environment-util';
import {
  PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS,
  PAYSDK_DEFAULT_DISABLED_CARD_TYPES,
  PAYSDK_TYPES,
  PAYSDK_PARTNER,
  PAYSDK_SCREEN_TYPE,
  PAYSDK_PROFILE_TYPES,
  PAYSDK_DEFAULT_CONTAINER_ID_PREFIX,
} from 'constants/paysdk-constants';

/**
 * Build serviceConfigs (shared by both PAYMENT_LIST and PAYMENT_RECOMMENDATION).
 * Default for `disabledCardTypes` is applied here only (nullish → PAYSDK_DEFAULT_DISABLED_CARD_TYPES).
 * Callers may pass [] to mean “do not disable any card types”.
 */
const buildServiceConfigs = (
  amount,
  enabledPaymentMethods = PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS,
  disabledCardTypes,
) => [
  {
    amount,
    enabledPaymentMethods,
    disabledCardTypes:
      disabledCardTypes != null
        ? disabledCardTypes
        : PAYSDK_DEFAULT_DISABLED_CARD_TYPES,
  },
];

/**
 * Compute total amount in PaySDK major format (display value).
 *
 * Backend returns `finalFare.lowerBound` in minor/raw format: display × 10^exponent.
 * PaySDK expects amount in major format (human-readable display value).
 * Conversion: lowerBound × 10^(-exponent) = lowerBound / 10^exponent.
 */
const computeAmount = deliveries => {
  const rawTotal = deliveries.reduce(
    (sum, delivery) =>
      sum + Number(_get(delivery, 'quote.finalFare.lowerBound', 0) || 0),
    0,
  );
  if (!rawTotal) return 0;

  const exponent = _get(deliveries, '[0].quote.currency.exponent');
  if (exponent == null) {
    const errorCtx = rumErrorCtx()
      .component('usePaySdk:computeAmount')
      .message(
        'Currency exponent missing from quote — cannot convert to major amount',
      )
      .severity(SEVERITY.MEDIUM)
      .build();
    ddRum.addError(new Error('Missing currency exponent'), errorCtx);
    return 0;
  }
  return Math.max(rawTotal / Math.pow(10, exponent), 0);
};

/**
 * Build the shared `params` block used by every universal_selection screen.
 * Spec fields: skipConfigBasedFilters, partner, orderCountryCode,
 * is_advanced_booking, userProfileType, userProfileID, templateID, serviceConfigs.
 */
const buildBaseParams = ({
  countryCode,
  amount,
  enabledPaymentMethods,
  enterprise,
  isAdvancedBooking,
  disabledCardTypes,
}) => {
  const rawGroupID = _get(enterprise, 'groupID');
  const userProfileID = rawGroupID != null ? String(rawGroupID) : '0';
  const rawProfileType = _get(enterprise, 'profileType');
  const profileTypeKey =
    rawProfileType != null && String(rawProfileType).trim() !== ''
      ? String(rawProfileType)
          .trim()
          .toUpperCase()
      : '';
  const userProfileType =
    PAYSDK_PROFILE_TYPES[profileTypeKey] || PAYSDK_PROFILE_TYPES.PERSONAL;

  return {
    skipConfigBasedFilters: true,
    partner: PAYSDK_PARTNER,
    orderCountryCode: countryCode || '',
    is_advanced_booking: Boolean(isAdvancedBooking),
    userProfileType,
    userProfileID,
    isUnmergedProfile: true,
    serviceConfigs: buildServiceConfigs(
      amount,
      enabledPaymentMethods,
      disabledCardTypes,
    ),
  };
};

/**
 * PAYMENT_LIST screenParams (per spec).
 * Extends the shared universal_selection params with `selectedPaymentTypeID`
 * so the drawer can pre-highlight the current selection.
 *
 * @param {Object} [options]
 * @param {Array<string>} [options.disabledCardTypes] - Omitted → default in {@link buildServiceConfigs}.
 *   Pass `[]` to send no disabled card types to PaySDK.
 */
export const buildPaymentListScreenParams = (
  { location, deliveries, countryCode, selectedPaymentTypeID, enterprise },
  options = {},
) => {
  const latitude = _get(location, 'latitude', 0);
  const longitude = _get(location, 'longitude', 0);
  const amount = computeAmount(deliveries);
  const enabledPaymentMethods =
    options.enabledPaymentMethods || PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS;

  const params = {
    ...buildBaseParams({
      countryCode,
      amount,
      enabledPaymentMethods,
      enterprise,
      isAdvancedBooking: options.isAdvancedBooking,
      disabledCardTypes: options.disabledCardTypes,
    }),
    selectedPaymentTypeID: selectedPaymentTypeID || '',
  };

  return {
    screenType: PAYSDK_SCREEN_TYPE,
    latitude,
    longitude,
    params,
    ...(options.pageConfig && { pageConfig: options.pageConfig }),
  };
};

/**
 * PAYMENT_RECOMMENDATION screenParams (per spec).
 * Same universal_selection shape as PAYMENT_LIST minus `selectedPaymentTypeID`.
 *
 * @param {Object} [options]
 * @param {Array<string>} [options.disabledCardTypes] - Omitted → default in {@link buildServiceConfigs}.
 *   Pass `[]` to send no disabled card types to PaySDK.
 */
export const buildPaymentRecommendationScreenParams = (
  { location, deliveries, countryCode, enterprise },
  options = {},
) => {
  const latitude = _get(location, 'latitude', 0);
  const longitude = _get(location, 'longitude', 0);
  const amount = computeAmount(deliveries);
  const enabledPaymentMethods =
    options.enabledPaymentMethods || PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS;

  const params = buildBaseParams({
    countryCode,
    amount,
    enabledPaymentMethods,
    enterprise,
    isAdvancedBooking: options.isAdvancedBooking,
    disabledCardTypes: options.disabledCardTypes,
  });

  return {
    screenType: PAYSDK_SCREEN_TYPE,
    latitude,
    longitude,
    params,
    ...(options.pageConfig && { pageConfig: options.pageConfig }),
  };
};

/**
 * Hook for PaySDK initialization and lifecycle management
 * @param {Object} params
 * @param {string} params.paySdkType - 'PAYMENT_LIST' or 'PAYMENT_RECOMMENDATION' (resolved via PaySDK.TYPES)
 * @param {Object} params.location
 * @param {Array} params.deliveries
 * @param {string} params.countryCode
 * @param {Object} params.enterprise
 * @param {string} params.selectedPaymentTypeID
 * @param {Array} [params.enabledPaymentMethods] - e.g. ['CARD'] or ['CASH', 'CARD']. Falls back to PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS.
 *   Remount behavior follows **serialized** list contents, not array reference—inline literals with the same values are stable.
 * @param {Array} [params.disabledCardTypes] - Card types to disable (e.g. ['OVO Points']).
 *   Omit to use PAYSDK_DEFAULT_DISABLED_CARD_TYPES. Pass [] to disable no card types (all enabled).
 *   Remount behavior follows **serialized** list contents, not array reference.
 * @param {Function} [params.onEvent] - callback for PaySDK events; attach via listen() at creation
 * @param {string} [params.stateScope] - UUID scoping PaySDK browser state; same scope for
 *   recommendation+list on the same delivery surface, different scope per delivery to prevent
 *   cross-instance events (see integration-guide.md#multiple-paysdk-widgets-on-one-page).
 * @returns {{ containerRef, containerId, paysdkInstanceRef, scriptLoaded, loadError }}
 */
export const usePaySdk = ({
  paySdkType,
  location,
  deliveries,
  countryCode,
  enterprise,
  selectedPaymentTypeID,
  enabledPaymentMethods = PAYSDK_DEFAULT_ENABLED_PAYMENT_METHODS,
  disabledCardTypes = PAYSDK_DEFAULT_DISABLED_CARD_TYPES,
  containerIdPrefix = PAYSDK_DEFAULT_CONTAINER_ID_PREFIX,
  onEvent,
  stateScope,
}) => {
  const userInfo = useSelector(userInfoSelector);
  // Content-based keys: inline `['OVO Points']` each render must not churn option identity.
  const enabledPaymentMethodsKey = JSON.stringify(enabledPaymentMethods);
  const disabledCardTypesKey = JSON.stringify(disabledCardTypes);
  const screenParamsOptions = useMemo(
    () => ({
      enabledPaymentMethods,
      disabledCardTypes,
      pageConfig: { showBackButton: false },
    }),
    [enabledPaymentMethodsKey, disabledCardTypesKey],
  );
  const containerRef = useRef(null);
  const paysdkInstanceRef = useRef(null);
  const initializedRef = useRef(false);
  const [scriptLoaded, setScriptLoaded] = useState(
    () => typeof window !== 'undefined' && !!window.PaySDK,
  );
  const [loadError, setLoadError] = useState(null);

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const quoteTotal = computeAmount(deliveries);
  const latitude = _get(location, 'latitude', 0);
  const longitude = _get(location, 'longitude', 0);
  const { userId: paySdkUserId } = getPaySdkContext(userInfo);
  const enterpriseGroupId = _get(enterprise, 'groupID');
  const enterpriseProfileType = _get(enterprise, 'profileType');

  const containerId = useMemo(
    () =>
      `${containerIdPrefix}-${Math.random()
        .toString(36)
        .slice(2)}`,
    [containerIdPrefix],
  );

  useEffect(() => {
    loadPaySdkScript()
      .then(() => setScriptLoaded(true))
      .catch(err => {
        setLoadError(err);
      });
  }, []);

  useEffect(
    () => {
      if (!scriptLoaded || !window.PaySDK || !containerRef.current) return;

      const paySdkTypeValue = _get(window, `PaySDK.TYPES.${paySdkType}`);
      if (!paySdkTypeValue) {
        setLoadError(new Error(`PaySDK type not available: ${paySdkType}`));
        return;
      }

      let isMounted = true;

      const initializePaySDK = () => {
        if (
          !isMounted ||
          initializedRef.current ||
          !containerRef.current ||
          !window.PaySDK
        ) {
          return;
        }

        const baseInput = {
          location,
          deliveries,
          countryCode,
          enterprise,
        };
        const screenParams =
          paySdkType === PAYSDK_TYPES.PAYMENT_LIST
            ? buildPaymentListScreenParams(
                {
                  ...baseInput,
                  userId: paySdkUserId,
                  selectedPaymentTypeID,
                },
                screenParamsOptions,
              )
            : buildPaymentRecommendationScreenParams(
                baseInput,
                screenParamsOptions,
              );

        // Pax flow: pass Bearer token explicitly (Method 1)
        // Enterprise flow: rely on automatic cookie auth (Method 2)
        const paxToken =
          !isAuthHintCookie() && isGrabIDAuthenticated()
            ? `Bearer ${getPaxAccessToken()}`
            : null;
        const paysdkInstance = new window.PaySDK({
          sandbox: false,
          debug: !isProduction(),
          ...(paxToken && { token: paxToken }),
          ...(isDev() && {
            apiBaseUrl: _get(window, 'Grab.config.endpoints.paysdkBaseUrl'),
          }),
          ...(stateScope && { stateScope }),
        });

        const mountChain = paysdkInstance
          .create(paySdkTypeValue, screenParams)
          .mount(`#${containerId}`);

        if (onEventRef.current) {
          mountChain.listen(event => {
            const cb = onEventRef.current;
            if (event && cb) cb(event);
          });
        }

        paysdkInstanceRef.current = paysdkInstance;
        initializedRef.current = true;
      };

      initializePaySDK();

      return () => {
        isMounted = false;
        if (paysdkInstanceRef.current) {
          paysdkInstanceRef.current.destroy();
          paysdkInstanceRef.current = null;
        }
        initializedRef.current = false;
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      };
    },
    [
      scriptLoaded,
      containerId,
      paySdkType,
      quoteTotal,
      latitude,
      longitude,
      countryCode,
      paySdkUserId,
      enterpriseGroupId,
      enterpriseProfileType,
      screenParamsOptions,
      stateScope,
    ],
  );

  return {
    containerRef,
    containerId,
    paysdkInstanceRef,
    scriptLoaded,
    loadError,
  };
};

export default usePaySdk;
